import { SidebarIcon } from './sidebarIcon'
import { Bars3Icon, ChevronLeftIcon, ChevronRightIcon, LockClosedIcon, MagnifyingGlassIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../context/authContext'
import { RouteElement } from '../../interfaces/_common'
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { SidebarSubMenu } from './SidebarSubMenu';
import { hasAccess } from 'constants/permission.utils'
import { filterMenuByPermission } from './menu.filter'


interface SidebarProps {
    routes: RouteElement[]
}
export const Sidebar: React.FC<SidebarProps> = ({ routes }) => {
    const auth = useAuth()
    const visibleRoutes = filterMenuByPermission(routes, auth?.user?.permissions || [])
    const [showSidebar, setShowSidebar] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    let pathname = useLocation().pathname
    const [isCollapsed, setIsCollapsed] = useState(true)
    let activeRoute = routes.filter(route => (route.end ? pathname === route.route : pathname?.includes(`${route.route}`)))?.[0]
    const isSearching = searchTerm.trim().length > 0
    const filteredRoutes = useMemo(() => {
        const term = searchTerm.trim().toLowerCase()

        if (!term) return visibleRoutes

        return visibleRoutes
            .map((route: RouteElement) => {
                const children = route.children?.filter(child => child.title.toLowerCase().includes(term))
                const parentMatches = route.title.toLowerCase().includes(term)

                if (parentMatches) return route
                if (children?.length) return { ...route, children }

                return null
            })
            .filter(Boolean) as RouteElement[]
    }, [searchTerm, visibleRoutes])

    return (
        <div className="shrink-0">
            {showSidebar && <div onClick={() => setShowSidebar(false)} className='fixed inset-0 z-30 bg-slate-950/55 backdrop-blur-[2px] md:hidden'></div>}
            <button onClick={() => setShowSidebar(true)} className='fixed left-3 top-3 z-20 inline-flex items-center gap-2 rounded-lg border border-white/70 bg-white/90 px-3 py-2 text-slate-800 shadow-lg shadow-slate-900/10 backdrop-blur md:hidden dark:border-slate-700 dark:bg-slate-900/90 dark:text-slate-100'>
                <Bars3Icon className='h-5 w-5' />
                <h4 className="max-w-[170px] truncate text-sm font-semibold">{activeRoute?.title || 'Menu'}</h4>
            </button>


            <aside className={`fixed md:sticky top-0 left-0
            border-r border-slate-200/80 bg-white shadow-2xl shadow-slate-900/10 dark:border-slate-700/70 dark:bg-slate-950 h-screen
            ${showSidebar ? 'flex' : 'hidden'} md:flex
            flex-col justify-between z-40 transition-all duration-300 ease-out`}
                style={{ width: showSidebar ? '280px' : isCollapsed ? '68px' : '248px' }}>
                <div className={`relative border-b border-slate-200/80 bg-slate-950 px-3 py-3 text-white dark:border-slate-800 ${isCollapsed && !showSidebar ? 'px-2' : ''}`}>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.38),transparent_26%),radial-gradient(circle_at_90%_15%,rgba(20,184,166,0.28),transparent_24%)]"></div>
                    <div className={`relative flex items-center ${isCollapsed && !showSidebar ? 'justify-center' : 'gap-3'}`}>
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-950 shadow-lg shadow-blue-950/20 ring-1 ring-white/60">
                            <SparklesIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        {(!isCollapsed || showSidebar) && (
                            <div className="min-w-0">
                                <p className="truncate text-sm font-bold leading-5">OTT CMS</p>
                                <p className="truncate text-[11px] font-medium text-blue-100">Production Console</p>
                            </div>
                        )}
                        {showSidebar && (
                            <button
                                onClick={() => setShowSidebar(false)}
                                className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-lg text-blue-100 transition hover:bg-white/10 hover:text-white md:hidden"
                                type="button"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                </div>

                {!showSidebar && (
                    <button
                        onClick={() => setIsCollapsed(prev => !prev)}
                        className={`sticky top-0 z-[40] hidden w-full items-center border-b border-slate-200/80 bg-white/95 px-3 py-2.5 text-slate-600 shadow-sm backdrop-blur transition hover:bg-slate-50 hover:text-blue-700 md:flex dark:border-slate-800 dark:bg-slate-950/95 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-blue-300 ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
                        {!isCollapsed && <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Navigation</span>}
                        {isCollapsed ? <ChevronRightIcon className="h-5 w-5 shrink-0" /> : <ChevronLeftIcon className="h-5 w-5 shrink-0" />}

                        {!isCollapsed && (
                            <span className="sr-only">Collapse</span>
                        )}
                    </button>
                )}
                {(!isCollapsed || showSidebar) && (
                    <div className="border-b border-slate-200/70 bg-white px-3 py-3 dark:border-slate-800 dark:bg-slate-950">
                        <label className="relative block">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder="Search menu"
                                className="h-9 w-full rounded-lg border-slate-200 bg-slate-50 pl-9 pr-8 text-xs font-medium text-slate-700 shadow-sm transition placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:bg-slate-900"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                                    type="button"
                                >
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </label>
                    </div>
                )}
                <div
                    className="sidebar-scrollbar-fancy flex w-full flex-1 flex-col overflow-y-auto overflow-x-hidden bg-slate-50/80 pb-4 pt-2 dark:bg-slate-950"
                >
                    {(!isCollapsed || showSidebar) && (
                        <div className="flex items-center justify-between px-3 pb-2 pt-1">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                                Menu
                            </span>
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/60 dark:text-blue-300 dark:ring-blue-900">
                                {filteredRoutes.length}
                            </span>
                        </div>
                    )}
                    {/* {visibleRoutes.map(route => (
                        <SidebarIcon disabled={!auth?.isAllowedRouteKey(route.key)} key={route.key} end={route.end} Icon={auth?.isAllowedRouteKey(route.key) ? (route.icon ?? Bars3Icon) : LockClosedIcon} label={route.title} linkTo={route.route} />
                    ))} */}
                    {filteredRoutes.map(route => {
                        if (route.children && route.children.length) {
                            return (
                                hasAccess(route.key, auth?.user?.permissions) && <SidebarSubMenu
                                    key={route.key}
                                    icon={route.icon ?? Bars3Icon}
                                    label={route.title}
                                    route={route.route}
                                    children={route.children}
                                    // disabled={!auth?.isAllowedRouteKey(route.key)}
                                    collapsed={isCollapsed && !showSidebar}
                                    forceOpen={isSearching}
                                />
                            )
                        }

                        return (
                            hasAccess(route.key, auth?.user?.permissions) && <SidebarIcon
                                key={route.key}
                                //disabled={!auth?.isAllowedRouteKey(route.key)}
                                end={route.end}
                                Icon={(route.key)
                                    ? (route.icon ?? Bars3Icon)
                                    : LockClosedIcon}
                                label={route.title}
                                linkTo={route.route}
                                collapsed={isCollapsed && !showSidebar}
                            />
                        )
                    })}
                    {filteredRoutes.length === 0 && (
                        <div className="mx-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-5 text-center text-xs font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                            No matching menu
                        </div>
                    )}

                </div>
                {(!isCollapsed || showSidebar) && (
                    <div className="border-t border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
                        <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 dark:border-emerald-900/60 dark:bg-emerald-950/40">
                            <p className="text-xs font-bold text-emerald-700 dark:text-emerald-300">System online</p>
                            <p className="mt-0.5 truncate text-[11px] text-emerald-700/70 dark:text-emerald-200/70">Role based access active</p>
                        </div>
                    </div>
                )}
            </aside>
        </div>
    )
}
